import 'regenerator-runtime/runtime';
import { SagaIterator } from 'redux-saga';
import { call, cancel, delay, put, retry, select, takeEvery } from 'redux-saga/effects';
import {
  DoRequest,
  DoRequestResponse,
  getEndpointUrl,
  GlobalAction,
  GlobalActions,
  GlobalEventDispatcher,
  HTTP_METHOD,
  storeIsAvailable,
  StreckenTarifierungRequestEvents,
} from '@ps20-frontend/shop-integration-redux';
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { IAppState } from '../../localStore';
import { createItemCachedAction } from '../../local.actions';
import {
  isApigeeActiveAndNotPermasession,
  isSdwSession,
} from '../../selectors/transientState.selector';
import { getHashedRequest, getResponseFromCache } from './cacheHandler';
import { getAxiosGetOptions, getAxiosPostOptions } from './axiosOptions';
import { handleBffVersionHasChanged } from './bffVersionChangedHandler';

export const DISPATCHED_DO_REQUEST_SUCCESSFULL = 'DISPATCHED_DO_REQUEST_SUCCESSFULL';
export const DISPATCHED_DO_REQUEST_ERROR = 'DISPATCHED_DO_REQUEST_ERROR';
export const DISPATCHED_BFF_HAS_CHANGED = 'DISPATCHED_BFF_HAS_CHANGED';
export const DISPATCHED_CACHED_RESPONSE = 'DISPATCHED_CACHED_RESPONSE';

export function* watchDoRequest(): SagaIterator {
  yield takeEvery(GlobalActions.DO_REQUEST, doRequestSaga);
}

export function* doRequestSaga(action: GlobalAction<DoRequest>): SagaIterator {
  if (isPostRequest(action)) {
    yield call(callPostAndHandleResponse, action);
  } else {
    yield call(callGetAndHandleResponse, action);
  }
}

function isPostRequest(action: GlobalAction<DoRequest>): boolean {
  return !action.payload.method || action.payload.method === HTTP_METHOD.POST;
}

export function* callPostAndHandleResponse(doRequestAction: GlobalAction<DoRequest>): SagaIterator {
  const state: IAppState = yield select();
  const doRequestPayload = doRequestAction.payload;
  const hashedData = getHashedRequest(doRequestPayload);
  const responseFromCache = getResponseFromCache(state, hashedData);
  if (responseFromCache && !doRequestPayload.noCache) {
    yield delay(0);
    yield call(dispatchDoRequestSuccessful, responseFromCache, doRequestAction);
    yield put({ type: DISPATCHED_CACHED_RESPONSE });
    return responseFromCache;
  }

  try {
    const options = getAxiosPostOptions(state, doRequestPayload);
    const resp = yield call(handleAxiosPost, doRequestPayload, options, state);
    yield call(handleBffVersionHasChanged, resp);
    return yield call(handlePostResponse, resp, hashedData, doRequestAction);
  } catch (e) {
    return yield call(handlePostErrors, e as unknown as AxiosError, doRequestAction);
  }
}

function getShouldUpdate(state: IAppState, payload: DoRequest) {
  return (
    isSdwSession(state) ||
    (isApigeeActiveAndNotPermasession(state) && !payload.url.includes('apigee'))
  );
}

function getShouldUpdateGet(state: IAppState, payload: DoRequest) {
  return isApigeeActiveAndNotPermasession(state) && !payload.url.includes('apigee');
}

function* handleAxiosPost(payload: DoRequest, options: AxiosRequestConfig, state: IAppState): SagaIterator {
  const times = payload.retry ? payload.retry.times : 1;
  const delayLength = payload.retry ? payload.retry.delay : 1;
  return yield retry(
    times,
    delayLength,
    axios.post,
    getEndpointUrl(getShouldUpdate(state, payload), payload.url),
    payload.body,
    options,
  );
}

function* handlePostResponse(
  resp: AxiosResponse,
  hashedData: string,
  doRequestAction: GlobalAction<DoRequest>,
): SagaIterator {
  yield put(createItemCachedAction({ data: resp.data, hashedData }));
  yield call(dispatchDoRequestSuccessful, resp.data, doRequestAction);
  yield put({ type: DISPATCHED_DO_REQUEST_SUCCESSFULL });
}

function* handlePostErrors(e: AxiosError, doRequestAction: GlobalAction<DoRequest>): SagaIterator {
  if (hasNetworkErrorOrIsUnauthorized(e)) {
    yield put(new GlobalActions().errorPageRequested());
    yield cancel();
  }

  const versionChanged = yield call(handleBffVersionHasChanged, e.response);
  if (versionChanged) {
    yield put({ type: DISPATCHED_BFF_HAS_CHANGED });
    yield cancel();
  }

  yield call(dispatchDoRequestFailed, e, doRequestAction);
  yield put({ type: DISPATCHED_DO_REQUEST_ERROR });
}

function hasNetworkErrorOrIsUnauthorized(e: AxiosError): boolean {
  return e.response == null || e.response.status === 401;
}

function shouldShowConstructionPage(e: AxiosError): boolean {
  return e.response?.status === 503;
}

export function* callGetAndHandleResponse(doRequestAction: GlobalAction<DoRequest>): SagaIterator {
  const state: IAppState = yield select();
  const doRequestPayload: DoRequest = doRequestAction.payload;

  try {
    const options = getAxiosGetOptions(state, doRequestPayload);
    const resp = yield call([axios, axios.get],
      getEndpointUrl(getShouldUpdateGet(state, doRequestPayload), doRequestPayload.url),
      options,
    );
    yield call(handleBffVersionHasChanged, resp);
    yield call(handleGetResponse, resp, doRequestAction);
  } catch (e) {
    yield call(dispatchDoRequestFailed, e as unknown as AxiosError, doRequestAction);
    yield put({ type: DISPATCHED_DO_REQUEST_ERROR });
  }
}

function* handleGetResponse(resp: AxiosResponse, doRequestAction: GlobalAction<DoRequest>): SagaIterator {
  yield call(dispatchDoRequestSuccessful, resp.data, doRequestAction);
  yield put({ type: DISPATCHED_DO_REQUEST_SUCCESSFULL });
}

export function dispatchDoRequestSuccessful(
  responseData: unknown,
  doRequestAction: GlobalAction<DoRequest>,
) {
  dispatchAnswer(new GlobalActions().doRequestSuccessful(responseData, doRequestAction));
}

export function* dispatchDoRequestFailed(
  errorMsg: AxiosError,
  doRequestAction: GlobalAction<DoRequest>,
): SagaIterator {
  if (shouldShowConstructionPage(errorMsg)) {
    yield put(new GlobalActions().constructionPageRequested());
    yield cancel();
  }

  dispatchAnswer(new GlobalActions().doRequestFailed(errorMsg, doRequestAction));
}

function dispatchAnswer(answer: DoRequestResponse<unknown>): void {
  const globalEventDispatcher = GlobalEventDispatcher.getInstance();
  const answerOfRemovedStore = isPricingAnswerOfRemovedStore(answer);
  globalEventDispatcher.dispatch(answer);
  if (answerOfRemovedStore) {
    globalEventDispatcher.dispatch(new GlobalActions().tarifierungRunReleased());
  }
}

function isPricingAnswerOfRemovedStore(answer: DoRequestResponse<unknown>): boolean {
  const { angebotsId, appId } = answer.doRequestAction.payload;
  return (
    !!angebotsId &&
    Object.values(StreckenTarifierungRequestEvents).includes(answer.type) &&
    !storeIsAvailable({ angebotsId, appId })
  );
}
